import { Link } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { GlassCard } from '@/components/ui/glass-card'
import { useLuxeHero, useLuxeReveal } from '@gjl/motion/css'
import { ArrowRight, Shield, Eye, Clock } from 'lucide-react'

export default function AboutPage() {
  const heroRef = useLuxeHero<HTMLDivElement>(0.2)
  const storyRef = useLuxeReveal<HTMLDivElement>('.reveal')
  const commitmentRef = useLuxeReveal<HTMLDivElement>('.reveal')

  return (
    <div className="bg-background text-foreground">
      {/* Hero Section */}
      <section className="relative overflow-hidden py-24 md:py-32">
        <div className="bg-gradient-dark-gold absolute inset-0" />
        <div ref={heroRef} className="relative z-10 mx-auto max-w-4xl px-4 text-center">
          <p className="text-primary mb-6 text-lg font-medium tracking-wide">About Fairmeadow Capital</p>
          <h1 className="text-fluid-h1 text-foreground mb-6">
            Building Wealth Shouldn't Require Sacrificing Your Time
          </h1>
          <p className="text-fluid-body-lg text-muted-foreground mx-auto max-w-2xl">
            That's the belief that led me to start Fairmeadow Capital.
          </p>
        </div>
      </section>

      {/* Founder Section */}
      <section className="px-4 py-16 md:py-24">
        <div className="mx-auto max-w-4xl">
          <div className="mb-12 flex flex-col items-center gap-8 md:flex-row md:items-start md:gap-12">
            <div className="relative shrink-0">
              <div className="ring-primary/20 h-56 w-56 overflow-hidden rounded-2xl shadow-2xl ring-4 md:h-64 md:w-64">
                <img
                  src="/headshot.webp"
                  alt="Garrett John Law, Founder of Fairmeadow Capital"
                  className="h-full w-full object-cover"
                />
              </div>
            </div>
            <div className="text-center md:text-left">
              <h2 className="text-foreground mb-2 text-3xl font-bold">Garrett John Law</h2>
              <p className="text-primary mb-4 text-lg">Founder, Fairmeadow Capital</p>
              <p className="text-fluid-body-lg text-muted-foreground">
                Software engineer by training. Real estate investor by choice. Building a firm that makes passive real
                estate accessible.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section ref={storyRef} className="px-4 py-16 md:py-24">
        <div className="mx-auto max-w-3xl space-y-12">
          <div className="reveal space-y-6">
            <h3 className="text-fluid-h3 text-foreground">The Problem I Saw</h3>
            <p className="text-fluid-body-lg text-muted-foreground">
              For years, I watched talented professionals around me work incredibly hard, earn good incomes, but
              struggle to build real wealth. Their money sat in volatile markets, vulnerable to every economic headline.
              They wanted real estate exposure but didn't have the time, knowledge, or capital to go it alone.
            </p>
            <p className="text-fluid-body-lg text-muted-foreground">
              I felt it myself. As a software engineer, I was good at what I did, but I knew trading hours for dollars
              had a ceiling. I wanted my money working for me, not just sitting in index funds hoping for the best.
            </p>
          </div>

          <div className="section-divider" />

          <div className="reveal space-y-6">
            <h3 className="text-fluid-h3 text-foreground">Why Real Estate, Why Cleveland</h3>
            <p className="text-fluid-body-lg text-muted-foreground">
              Real estate made sense to me because of the control. I could walk through a property, verify the income,
              underwrite the expenses, and manage the outcome directly. When I started researching markets, everyone was
              chasing the same hot cities - Austin, Nashville, Boise. High prices, low yields, massive competition.
            </p>
            <p className="text-fluid-body-lg text-muted-foreground">
              Then I looked at Cleveland. A city most investors overlook. But the numbers told a different story: stable
              population, growing economy, affordable properties, strong cash flow. Most importantly, I could understand
              this market deeply rather than competing with thousands of investors who knew it better than I ever would.
            </p>
            <p className="text-fluid-body-lg text-muted-foreground">
              I bought my first duplex in April 2025 in the West Boulevard neighborhood. Nothing fancy - just a solid,
              occupied property generating real cash flow from day one. That deal taught me more than any book or
              podcast ever could.
            </p>
          </div>

          <div className="section-divider" />

          <div className="reveal space-y-6">
            <h3 className="text-fluid-h3 text-foreground">The Insight That Changed Everything</h3>
            <p className="text-fluid-body-lg text-muted-foreground">
              Here's what I realized: the people who needed real estate exposure the most - busy professionals with
              capital but no time - had the hardest time accessing good deals. They didn't want to become landlords.
              They didn't want to learn property management. They just wanted the benefits: passive income, inflation
              protection, portfolio diversification, tax advantages.
            </p>
            <p className="text-fluid-body-lg text-muted-foreground">
              I had the time, the analytical mindset from years in tech, and the willingness to do the work. They had
              the capital and the desire for better returns. It was obvious: this could be a partnership, not a
              transaction.
            </p>
          </div>

          <div className="section-divider" />

          <div className="reveal space-y-6">
            <h3 className="text-fluid-h3 text-foreground">What Fairmeadow Capital Stands For</h3>
            <p className="text-fluid-body-lg text-muted-foreground">
              I named the company Fairmeadow Capital because I wanted a name that felt grounded, natural, and
              trustworthy. Not flashy or aggressive. Just steady growth, like a meadow returning every spring.
            </p>
            <p className="text-fluid-body-lg text-muted-foreground">
              This isn't about getting rich quick. It's about building real wealth through real assets, one
              conservative, well-underwritten deal at a time. It's about giving busy professionals the same
              opportunities I wanted for myself.
            </p>
          </div>
        </div>
      </section>

      {/* Section Divider */}
      <div className="section-divider mx-auto max-w-4xl" />

      {/* Commitment Section */}
      <section ref={commitmentRef} className="px-4 py-16 md:py-24">
        <div className="mx-auto max-w-5xl">
          <div className="mb-12 text-center">
            <h2 className="text-fluid-h2 text-foreground mb-4">My Commitment to You</h2>
            <p className="text-muted-foreground mx-auto max-w-2xl text-lg">
              Fairmeadow Capital is built on three non-negotiables
            </p>
          </div>

          <div className="reveal grid gap-6 md:grid-cols-3">
            <GlassCard hover className="text-center">
              <div className="bg-primary/10 mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-xl">
                <Shield className="text-primary h-7 w-7" />
              </div>
              <h3 className="text-foreground mb-3 text-xl font-semibold">Protect Capital First</h3>
              <p className="text-muted-foreground">
                Every deal must pass conservative stress tests. If I wouldn't invest my own money, I won't ask you to
                invest yours.
              </p>
            </GlassCard>

            <GlassCard hover className="text-center">
              <div className="bg-primary/10 mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-xl">
                <Eye className="text-primary h-7 w-7" />
              </div>
              <h3 className="text-foreground mb-3 text-xl font-semibold">Full Investor Visibility</h3>
              <p className="text-muted-foreground">
                Quarterly reports with property-level financials, occupancy data, and market analysis. You'll always
                know how your investment is performing.
              </p>
            </GlassCard>

            <GlassCard hover className="text-center">
              <div className="bg-primary/10 mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-xl">
                <Clock className="text-primary h-7 w-7" />
              </div>
              <h3 className="text-foreground mb-3 text-xl font-semibold">Long-Term Thinking</h3>
              <p className="text-muted-foreground">
                We're building a portfolio that works in decades, not quarters. Slow, steady, and resilient.
              </p>
            </GlassCard>
          </div>

          <div className="mx-auto mt-12 max-w-3xl">
            <GlassCard className="reveal">
              <p className="text-fluid-body-lg text-muted-foreground">
                I'm not trying to build the biggest real estate empire. I'm trying to build the right one - one that
                serves its investors well, treats its residents fairly, and contributes positively to the communities
                where we invest.
              </p>
            </GlassCard>
          </div>
        </div>
      </section>

      {/* Section Divider */}
      <div className="section-divider mx-auto max-w-4xl" />

      {/* Beyond Real Estate */}
      <section className="px-4 py-16 md:py-24">
        <div className="mx-auto max-w-3xl">
          <h3 className="text-fluid-h3 text-foreground mb-6 text-center">Beyond Real Estate</h3>
          <GlassCard>
            <p className="text-fluid-body-lg text-muted-foreground mb-6">
              When I'm not analyzing deals or managing properties, you'll find me playing piano (I'm a professional
              classical pianist and I direct a music festival in Hungary), coding (I still love building software), or
              exploring Cleveland's neighborhoods with my wife.
            </p>
            <p className="text-foreground text-lg font-medium">
              Real estate investing taught me that the best opportunities aren't where everyone else is looking. The
              same is true in life.
            </p>
            <p className="text-muted-foreground mt-6">
              More about my work in software and music lives at{' '}
              <a
                href="https://garrettjohnlaw.com"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary font-medium underline-offset-4 hover:underline"
              >
                garrettjohnlaw.com
              </a>
              .
            </p>
          </GlassCard>
        </div>
      </section>

      {/* CTA Section */}
      <section className="px-4 py-16 md:py-24">
        <div className="mx-auto max-w-4xl">
          <GlassCard accent className="text-center">
            <h2 className="text-fluid-h2 text-foreground mb-4">Ready to Start Building Wealth Together?</h2>
            <p className="text-muted-foreground mx-auto mb-8 max-w-2xl text-lg">
              Join me and other busy professionals who are building long-term wealth through conservative,
              well-underwritten real estate.
            </p>
            <Link to="/invest">
              <Button size="xl">
                Learn How to Partner With Us
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </GlassCard>
        </div>
      </section>
    </div>
  )
}
